import { useEffect, useId, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, errorMessage, post, put } from "@/lib/api";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import { EVENT_TYPES, typeLabel } from "@/utils/eventFormat";
import "./EventPages.css";

/**
 * Create / edit one event (events spec, "Event Creation UI").
 * Learn checks events.ManageEvents insert or edit, then POST / PUT goes to UIS
 * through Learn with the full targets array. No attendance rows are created.
 * Targets: specific people, and/or a group with an optional period and role.
 */

type TargetDraft = {
  key: string;
  ugpId?: string;
  groupId?: string;
  periodId?: string;
  role?: string;
  label: string;
  detail: string;
};

type SchoolOption = { id: string; name: string };
type PeriodOption = { id: string; title: string; permanent: boolean; startDate: string | null };
type PersonHit = { ugpId: string; name: string; detail: string };
type GroupHit = { id: string; name: string };

type StoredTarget = {
  ugp_id?: string | null;
  group_id?: string | null;
  period_id?: string | null;
  role?: string | null;
};

type StoredEvent = {
  id: string;
  school_id: string;
  type: string;
  title: string;
  description: string | null;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  location: string | null;
  mode: string;
  require_rsvp: boolean;
  max_capacity: number | null;
  targets: StoredTarget[];
};

const MODES = [
  {
    value: "attendance",
    label: "Attendance",
    hint: "Staff mark who came. No RSVP.",
  },
  {
    value: "rsvp",
    label: "RSVP",
    hint: "People answer yes, no, or maybe. No attendance is taken.",
  },
  {
    value: "rsvp_and_attendance",
    label: "RSVP, then attendance",
    hint: "People RSVP first. Staff take attendance at the event.",
  },
];

const ROLES = [
  { value: "", label: "Any role" },
  { value: "student", label: "Students" },
  { value: "parent", label: "Parents" },
  { value: "teacher", label: "Teachers" },
  { value: "counselor", label: "Counselors" },
];

function clock(value: string | null) {
  return value ? value.slice(0, 5) : "";
}

function roleWord(role: string | undefined) {
  return ROLES.find((item) => item.value === role)?.label || "";
}

function groupDetail(periodTitle: string, role: string | undefined) {
  return [periodTitle || "All periods", role ? `${roleWord(role).toLowerCase()} only` : "any role"].join(" · ");
}

export default function EventForm() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { pageAccess, session, loading: authLoading } = useAuth();
  const editing = Boolean(eventId);
  const canOpen = Boolean(pageAccess?.events);
  const verb = editing ? session?.acuityLearn?.eventActions?.edit : session?.acuityLearn?.eventActions?.insert;
  const ids = {
    school: useId(),
    type: useId(),
    title: useId(),
    description: useId(),
    startDate: useId(),
    startTime: useId(),
    endDate: useId(),
    endTime: useId(),
    location: useId(),
    capacity: useId(),
    person: useId(),
    group: useId(),
    period: useId(),
    role: useId(),
    whenHint: useId(),
    capacityHint: useId(),
  };

  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [schoolId, setSchoolId] = useState("");
  const [type, setType] = useState("assembly");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [location, setLocation] = useState("");
  const [mode, setMode] = useState("attendance");
  const [requireRsvp, setRequireRsvp] = useState(false);
  const [capacity, setCapacity] = useState("");
  const [targets, setTargets] = useState<TargetDraft[]>([]);
  const [targetsTouched, setTargetsTouched] = useState(false);
  const [storedTargets, setStoredTargets] = useState<StoredTarget[] | null>(null);
  const [rosterNames, setRosterNames] = useState<Map<string, PersonHit>>(new Map());
  const [periods, setPeriods] = useState<PeriodOption[]>([]);
  const [personQuery, setPersonQuery] = useState("");
  const [groupQuery, setGroupQuery] = useState("");
  const [groupPeriod, setGroupPeriod] = useState("");
  const [groupRole, setGroupRole] = useState("");
  const [people, setPeople] = useState<PersonHit[]>([]);
  const [groups, setGroups] = useState<GroupHit[]>([]);
  const [error, setError] = useState("");
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await api<{ success: boolean; data: { schools: SchoolOption[]; schoolId: string | null } }>(
          "/api/events",
        );
        if (cancelled) return;
        const rows = list.data?.schools || [];
        setSchools(rows);
        if (!eventId && rows.length === 1) setSchoolId(rows[0].id);
        if (!eventId) return;
        const detail = await api<{ success: boolean; data: { event: StoredEvent; roster?: PersonHit[] } }>(
          `/api/events/${encodeURIComponent(eventId)}`,
        );
        const event = detail.data?.event;
        if (!event || cancelled) return;
        setSchoolId(event.school_id || "");
        setType(event.type || "other");
        setTitle(event.title || "");
        setDescription(event.description || "");
        setStartDate(event.start_date ? event.start_date.slice(0, 10) : "");
        setEndDate(event.end_date ? event.end_date.slice(0, 10) : "");
        setStartTime(clock(event.start_time));
        setEndTime(clock(event.end_time));
        setLocation(event.location || "");
        setMode(event.mode || "attendance");
        setRequireRsvp(Boolean(event.require_rsvp));
        setCapacity(event.max_capacity == null ? "" : String(event.max_capacity));
        setRosterNames(new Map((detail.data?.roster || []).map((person) => [person.ugpId, person])));
        setStoredTargets(event.targets || []);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canOpen, eventId]);

  // Periods for the school, used by the Group + Period target.
  useEffect(() => {
    if (!schoolId || !canOpen) {
      setPeriods([]);
      return;
    }
    let cancelled = false;
    const query = new URLSearchParams({ school_id: schoolId });
    api<{ success: boolean; data: PeriodOption[] }>(`/api/events/periods?${query.toString()}`)
      .then((envelope) => {
        if (!cancelled) setPeriods(envelope.data || []);
      })
      .catch(() => {
        if (!cancelled) setPeriods([]);
      });
    return () => {
      cancelled = true;
    };
  }, [schoolId, canOpen]);

  // Turn stored targets into rows once names and periods are known. Group names are not
  // returned with an event, so a saved group shows as "Group" with its period and role.
  useEffect(() => {
    if (!storedTargets || targetsTouched) return;
    const periodTitle = new Map(periods.map((period) => [period.id, period.title]));
    setTargets(
      storedTargets.map((target, index) => {
        if (target.ugp_id) {
          const person = rosterNames.get(String(target.ugp_id));
          return {
            key: String(target.ugp_id),
            ugpId: String(target.ugp_id),
            role: target.role || "",
            label: person?.name || "Person",
            detail: person?.detail || "Added by name",
          };
        }
        const periodId = target.period_id ? String(target.period_id) : "";
        return {
          key: `${target.group_id}:${periodId}:${target.role || ""}:${index}`,
          groupId: target.group_id ? String(target.group_id) : undefined,
          periodId: periodId || undefined,
          role: target.role || "",
          label: "Group",
          detail: groupDetail(periodId ? periodTitle.get(periodId) || "One period" : "", target.role || ""),
        };
      }),
    );
  }, [storedTargets, rosterNames, periods, targetsTouched]);

  useEffect(() => {
    if (!schoolId || personQuery.trim().length < 2) {
      setPeople([]);
      return;
    }
    const handle = window.setTimeout(() => {
      const query = new URLSearchParams({ q: personQuery.trim(), school_id: schoolId });
      api<{ success: boolean; data: PersonHit[] }>(`/api/events/people?${query.toString()}`)
        .then((envelope) => setPeople(envelope.data || []))
        .catch(() => setPeople([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [personQuery, schoolId]);

  useEffect(() => {
    if (!schoolId || groupQuery.trim().length < 2) {
      setGroups([]);
      return;
    }
    const handle = window.setTimeout(() => {
      const query = new URLSearchParams({ q: groupQuery.trim(), school_id: schoolId });
      api<{ success: boolean; data: GroupHit[] }>(`/api/events/groups?${query.toString()}`)
        .then((envelope) => setGroups(envelope.data || []))
        .catch(() => setGroups([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [groupQuery, schoolId]);

  const problems = useMemo(() => {
    const out: Record<string, string> = {};
    if (!title.trim()) out.title = "Add a title.";
    if (startTime && !startDate) out.startTime = "A start time needs a start date.";
    if (endTime && !endDate && !startDate) out.endTime = "An end time needs a date.";
    if (startDate && endDate && endDate < startDate) out.endDate = "The end date can't be before the start date.";
    const sameDay = startDate && (!endDate || endDate === startDate);
    if (!out.endDate && sameDay && startTime && endTime && endTime < startTime) {
      out.endTime = "On the same day, the end time must be at or after the start time.";
    }
    if (capacity.trim()) {
      const n = Number(capacity);
      if (!Number.isInteger(n) || n < 1) out.capacity = "Use a whole number of at least 1, or leave it blank.";
    }
    if (schools.length > 1 && !schoolId) out.school = "Pick a school.";
    return out;
  }, [title, startDate, startTime, endDate, endTime, capacity, schools.length, schoolId]);

  if (authLoading) return null;
  if (!canOpen) return <Navigate to="/" replace state={accessDeniedNavigateState("events")} />;
  if (!verb) return <Navigate to="/events" replace state={accessDeniedNavigateState("events")} />;

  const backTo = editing && eventId ? `/events/${encodeURIComponent(eventId)}` : "/events";
  const hasRsvp = mode !== "attendance";
  const show = (key: string) => (tried || key === "endTime" || key === "endDate" || key === "startTime") && problems[key];

  const addTarget = (row: TargetDraft) => {
    setTargetsTouched(true);
    setTargets((current) => (current.some((item) => item.key === row.key) ? current : [...current, row]));
    setPeople([]);
    setGroups([]);
    setPersonQuery("");
    setGroupQuery("");
  };

  const save = async () => {
    setTried(true);
    if (Object.keys(problems).length) return;
    setSaving(true);
    setError("");
    const body = {
      school_id: schoolId,
      type,
      title: title.trim(),
      description: description.trim() || null,
      start_date: startDate || null,
      end_date: endDate || null,
      start_time: startTime || null,
      end_time: endTime || null,
      location: location.trim() || null,
      mode,
      require_rsvp: hasRsvp && requireRsvp,
      max_capacity: capacity.trim() ? Number(capacity) : null,
      targets: targets.map((target) => ({
        ugp_id: target.ugpId || null,
        group_id: target.groupId || null,
        period_id: target.periodId || null,
        role: target.role || null,
      })),
    };
    try {
      if (editing && eventId) {
        await put(`/api/events/${encodeURIComponent(eventId)}`, body);
        navigate(`/events/${encodeURIComponent(eventId)}`);
      } else {
        const envelope = await post<{ success: boolean; data: { id: string } }>("/api/events", body);
        const id = envelope.data?.id;
        navigate(id ? `/events/${encodeURIComponent(id)}` : "/events");
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key: string, id: string) =>
    show(key) ? (
      <p className="al-ev-field-error" id={`${id}-err`}>
        {problems[key]}
      </p>
    ) : null;

  const describedBy = (key: string, id: string, hint?: string) =>
    [show(key) ? `${id}-err` : "", hint || ""].filter(Boolean).join(" ") || undefined;

  const capacityHint =
    mode === "attendance"
      ? "Leave blank for no limit. Counts people marked Present."
      : "Leave blank for no limit. Counts RSVP yes; when full, new yes answers are refused.";

  return (
    <div className="al-ev al-ev--form">
      <Link className="al-ev-back" to={backTo}>
        <ChevronLeft size={14} aria-hidden="true" />
        {editing ? "Back to event" : "All events"}
      </Link>

      <header className="al-ev-head al-ev-head--detail">
        <div>
          <h1>{editing ? "Edit event" : "New event"}</h1>
          <p className="al-ev-sub">Only the type, title, and mode are required.</p>
        </div>
      </header>

      {tried && Object.keys(problems).length ? (
        <p className="al-ev-error" role="alert">
          Fix the fields marked below, then save again.
        </p>
      ) : null}
      {error ? (
        <p className="al-ev-error" role="alert">
          {error}
        </p>
      ) : null}

      <form
        className="al-ev-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <section className="al-ev-form-section" aria-labelledby="ev-basics">
          <h2 id="ev-basics">Basics</h2>
          {schools.length > 1 ? (
            <div className="al-ev-field">
              <label htmlFor={ids.school}>School</label>
              <select
                id={ids.school}
                value={schoolId}
                onChange={(event) => setSchoolId(event.target.value)}
                disabled={editing}
                aria-invalid={Boolean(show("school"))}
                aria-describedby={describedBy("school", ids.school)}
              >
                <option value="">Pick a school</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
              {fieldError("school", ids.school)}
            </div>
          ) : null}
          <div className="al-ev-row">
            <div className="al-ev-field al-ev-field--narrow">
              <label htmlFor={ids.type}>Type</label>
              <select id={ids.type} value={type} onChange={(event) => setType(event.target.value)}>
                {EVENT_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {typeLabel(value)}
                  </option>
                ))}
              </select>
            </div>
            <div className="al-ev-field al-ev-field--grow">
              <label htmlFor={ids.title}>Title</label>
              <input
                id={ids.title}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                aria-invalid={Boolean(show("title"))}
                aria-describedby={describedBy("title", ids.title)}
              />
              {fieldError("title", ids.title)}
            </div>
          </div>
          <div className="al-ev-field">
            <label htmlFor={ids.description}>
              Description <span className="al-ev-optional">optional</span>
            </label>
            <textarea
              id={ids.description}
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
        </section>

        <section className="al-ev-form-section" aria-labelledby="ev-when">
          <h2 id="ev-when">When and where</h2>
          <p className="al-ev-note" id={ids.whenHint}>
            All optional. A start time needs a start date. An event that ends on a later day may end at an
            earlier clock time.
          </p>
          <div className="al-ev-grid">
            <div className="al-ev-field">
              <label htmlFor={ids.startDate}>Start date</label>
              <input id={ids.startDate} type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </div>
            <div className="al-ev-field">
              <label htmlFor={ids.startTime}>Start time</label>
              <input
                id={ids.startTime}
                type="time"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
                aria-invalid={Boolean(show("startTime"))}
                aria-describedby={describedBy("startTime", ids.startTime)}
              />
              {fieldError("startTime", ids.startTime)}
            </div>
            <div className="al-ev-field">
              <label htmlFor={ids.endDate}>End date</label>
              <input
                id={ids.endDate}
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(event) => setEndDate(event.target.value)}
                aria-invalid={Boolean(show("endDate"))}
                aria-describedby={describedBy("endDate", ids.endDate)}
              />
              {fieldError("endDate", ids.endDate)}
            </div>
            <div className="al-ev-field">
              <label htmlFor={ids.endTime}>End time</label>
              <input
                id={ids.endTime}
                type="time"
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
                aria-invalid={Boolean(show("endTime"))}
                aria-describedby={describedBy("endTime", ids.endTime)}
              />
              {fieldError("endTime", ids.endTime)}
            </div>
          </div>
          <div className="al-ev-field">
            <label htmlFor={ids.location}>
              Location <span className="al-ev-optional">optional</span>
            </label>
            <input id={ids.location} value={location} onChange={(event) => setLocation(event.target.value)} />
          </div>
        </section>

        <section className="al-ev-form-section" aria-labelledby="ev-mode">
          <h2 id="ev-mode">How people take part</h2>
          <fieldset className="al-ev-modes">
            <legend className="al-ev-sr">Mode</legend>
            {MODES.map((option) => (
              <label key={option.value} className={mode === option.value ? "is-on" : ""}>
                <input
                  type="radio"
                  name="mode"
                  value={option.value}
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                />
                <span>
                  <span className="al-ev-strong">{option.label}</span>
                  <span className="al-ev-muted">{option.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {hasRsvp ? (
            <label className="al-ev-check">
              <input type="checkbox" checked={requireRsvp} onChange={(event) => setRequireRsvp(event.target.checked)} />
              <span>
                <span className="al-ev-strong">Require an RSVP</span>
                <span className="al-ev-muted">Staff see who has not answered on the event roster.</span>
              </span>
            </label>
          ) : null}
          <div className="al-ev-field al-ev-field--narrow">
            <label htmlFor={ids.capacity}>
              Max capacity <span className="al-ev-optional">optional</span>
            </label>
            <input
              id={ids.capacity}
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={capacity}
              placeholder="No limit"
              onChange={(event) => setCapacity(event.target.value)}
              aria-invalid={Boolean(show("capacity"))}
              aria-describedby={describedBy("capacity", ids.capacity, ids.capacityHint)}
            />
            <p className="al-ev-note" id={ids.capacityHint}>
              {capacityHint}
            </p>
            {fieldError("capacity", ids.capacity)}
          </div>
        </section>

        <section className="al-ev-form-section" aria-labelledby="ev-who">
          <h2 id="ev-who">Who it's for</h2>
          <p className="al-ev-note">
            Add specific people, a group, or both. Anyone who matches shows on the roster. You can save without
            anyone and add people later.
          </p>

          {targets.length ? (
            <ul className="al-ev-targets" aria-label="Added">
              {targets.map((target) => (
                <li key={target.key}>
                  <span>
                    <span className="al-ev-strong">{target.label}</span>
                    <span className="al-ev-muted">{target.detail}</span>
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${target.label}, ${target.detail}`}
                    onClick={() => {
                      setTargetsTouched(true);
                      setTargets((current) => current.filter((item) => item.key !== target.key));
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="al-ev-targets-empty">No one added yet.</p>
          )}

          <div className="al-ev-pickers">
            <div className="al-ev-picker">
              <h3>Add a person</h3>
              <p className="al-ev-note">Common for appointments.</p>
              <div className="al-ev-field">
                <label htmlFor={ids.person}>Name</label>
                <div className="al-ev-search">
                  <Search size={15} aria-hidden="true" />
                  <input
                    id={ids.person}
                    value={personQuery}
                    placeholder={schoolId ? "Type at least 2 letters" : "Pick a school first"}
                    disabled={!schoolId}
                    onChange={(event) => setPersonQuery(event.target.value)}
                  />
                </div>
              </div>
              {people.length ? (
                <ul className="al-ev-hits" aria-label="People found">
                  {people.map((person) => (
                    <li key={person.ugpId}>
                      <button
                        type="button"
                        onClick={() =>
                          addTarget({
                            key: person.ugpId,
                            ugpId: person.ugpId,
                            label: person.name,
                            detail: person.detail || "Added by name",
                          })
                        }
                      >
                        <span>
                          <span className="al-ev-strong">{person.name}</span>
                          {person.detail ? <span className="al-ev-muted">{person.detail}</span> : null}
                        </span>
                        <span className="al-ev-add">Add</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="al-ev-picker">
              <h3>Add a group</h3>
              <p className="al-ev-note">Common for conferences and volunteer days, e.g. students of one year.</p>
              <div className="al-ev-row">
                <div className="al-ev-field al-ev-field--grow">
                  <label htmlFor={ids.period}>Period</label>
                  <select id={ids.period} value={groupPeriod} onChange={(event) => setGroupPeriod(event.target.value)}>
                    <option value="">All periods</option>
                    {periods.map((period) => (
                      <option key={period.id} value={period.id}>
                        {period.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="al-ev-field al-ev-field--grow">
                  <label htmlFor={ids.role}>Role</label>
                  <select id={ids.role} value={groupRole} onChange={(event) => setGroupRole(event.target.value)}>
                    {ROLES.map((role) => (
                      <option key={role.value || "any"} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="al-ev-field">
                <label htmlFor={ids.group}>Group</label>
                <div className="al-ev-search">
                  <Search size={15} aria-hidden="true" />
                  <input
                    id={ids.group}
                    value={groupQuery}
                    placeholder={schoolId ? "Type at least 2 letters" : "Pick a school first"}
                    disabled={!schoolId}
                    onChange={(event) => setGroupQuery(event.target.value)}
                  />
                </div>
              </div>
              {groups.length ? (
                <ul className="al-ev-hits" aria-label="Groups found">
                  {groups.map((group) => {
                    const periodTitle = periods.find((period) => period.id === groupPeriod)?.title || "";
                    return (
                      <li key={group.id}>
                        <button
                          type="button"
                          onClick={() =>
                            addTarget({
                              key: `${group.id}:${groupPeriod}:${groupRole}`,
                              groupId: group.id,
                              periodId: groupPeriod || undefined,
                              role: groupRole,
                              label: group.name,
                              detail: groupDetail(periodTitle, groupRole),
                            })
                          }
                        >
                          <span>
                            <span className="al-ev-strong">{group.name}</span>
                            <span className="al-ev-muted">{groupDetail(periodTitle, groupRole)}</span>
                          </span>
                          <span className="al-ev-add">Add</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </div>
        </section>

        <div className="al-ev-form-actions">
          <Link className="al-ev-button" to={backTo}>
            Cancel
          </Link>
          <button className="al-ev-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : "Create event"}
          </button>
        </div>
      </form>
    </div>
  );
}
